'use client'

import { formatFontFamily } from '@/lib/themeHelpers'

interface FooterSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  iconWeight: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  logoUrl: string;
  contactInfo: { phone: string; email: string; address: string };
  socialLinks: { linkedin: string; twitter: string; facebook: string };
  companyName: string;
  description: string;
}

export default function FooterSection({
  surface,
  theme,
  radius,
  fontFamily,
  gradStart,
  gradEnd,
  angle,
  logoUrl,
  contactInfo,
  socialLinks,
  companyName,
  description
}: FooterSectionProps) {
  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative w-full" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-8">
        {/* Main Footer Content - Modified structure without navigation */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 mb-12">
          
          {/* Company Info Column - spans 2 columns */}
          <div className="lg:col-span-2">
            {/* Logo/Company Name */}
            <div className="mb-6">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={companyName} 
                  className="h-12 w-auto"
                  style={{ borderRadius: radius / 2 }}
                />
              ) : (
                <div 
                  className="h-12 w-48 flex items-center justify-center text-lg font-bold font-primary"
                  style={{ 
                    color: theme.text,
                    backgroundColor: theme.card,
                    borderRadius: radius / 2
                  }}
                >
                  {companyName}
                </div>
              )}
            </div>
            
            {/* Description */}
            <p className="text-sm mb-6 max-w-md font-secondary" style={{ color: theme.mutedText }}>
              {description}
            </p>
            
            {/* Contact Info - In company section like original */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" fill="currentColor" style={{ color: '#FFDD00' }} viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <span className="text-sm font-primary" style={{ color: theme.text }}>
                  {contactInfo.phone}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" fill="currentColor" style={{ color: '#FFDD00' }} viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span className="text-sm font-primary" style={{ color: theme.text }}>
                  {contactInfo.email}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" fill="currentColor" style={{ color: '#FFDD00' }} viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-primary" style={{ color: theme.text }}>
                  {contactInfo.address}
                </span>
              </div>
            </div>
          </div>
          
          {/* Social & Newsletter Column */}
          <div>
            <h3 className="text-sm font-semibold mb-4 font-primary" style={{ color: theme.text }}>
              Connect
            </h3>
            
            {/* Social Links */}
            <div className="flex items-center gap-3 mb-6">
              {socialLinks.linkedin && (
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 transition-colors duration-200 hover:opacity-70"
                  style={{
                    backgroundColor: theme.card,
                    borderRadius: radius / 2
                  }}
                >
                  <svg className="w-5 h-5" fill="currentColor" style={{ color: '#FFDD00' }} viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                  </svg>
                </a>
              )}
              {socialLinks.twitter && (
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 transition-colors duration-200 hover:opacity-70"
                  style={{
                    backgroundColor: theme.card,
                    borderRadius: radius / 2
                  }}
                >
                  <svg className="w-5 h-5" fill="currentColor" style={{ color: '#FFDD00' }} viewBox="0 0 20 20">
                    <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
              )}
              {socialLinks.facebook && (
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 transition-colors duration-200 hover:opacity-70"
                  style={{
                    backgroundColor: theme.card,
                    borderRadius: radius / 2
                  }}
                >
                  <svg className="w-5 h-5" fill="currentColor" style={{ color: '#FFDD00' }} viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clipRule="evenodd" />
                  </svg>
                </a>
              )}
            </div>
            
          </div>
        </div>
        
        {/* Bottom Footer - Match original */}
        <div 
          className="pt-8 border-t flex flex-col items-center gap-4"
          style={{ borderColor: theme.border }}
        >
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center font-primary" style={{ color: theme.mutedText, fontSize: '16px' }}>
            <span>© {currentYear} {companyName}. All rights reserved.</span>
            <span>Powered by <a href="https://empowerlocal.com" target="_blank" rel="noopener noreferrer" style={{ color: '#FFDD00', textDecoration: 'none' }}>EmpowerLocal</a></span>
          </div>
          <div className="flex items-center gap-6">
            <button className="text-xs transition-colors duration-200 hover:opacity-70 font-secondary" style={{ color: theme.mutedText }}>
              Privacy Policy
            </button>
            <button className="text-xs transition-colors duration-200 hover:opacity-70 font-secondary" style={{ color: theme.mutedText }}>
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
